import { JSX, ReactNode, createContext, useContext, useMemo } from "react";

import { memoize } from "@plansoft-configuration-renderer/utils";

const WARNING =
  "SubconfigurationNavigationApi used outside of SubconfigurationNavigation";

const warnOnce = memoize((message: string): void => {
  console.warn(message);
});

const SubconfigurationNavigationApiContext =
  createContext<SubconfigurationNavigationApi>({
    previous: () => warnOnce(WARNING),
    next: () => warnOnce(WARNING),
    get nextDisabled() {
      warnOnce(WARNING);
      return false;
    },
    get previousDisabled() {
      warnOnce(WARNING);
      return false;
    },
  });

SubconfigurationNavigationApiContext.displayName =
  "SubconfigurationNavigationApiContext";

export interface SubconfigurationNavigationApi {
  next(): void;
  nextDisabled: boolean;
  previous(): void;
  previousDisabled: boolean;
}

/**
 * Retrieves the {@link SubconfigurationNavigationApi} from context. This works only inside the react tree of {@link SubconfigurationNavigationApiProvider}.
 * @returns
 */
export function useSubconfigurationNavigationApi(): SubconfigurationNavigationApi {
  return useContext(SubconfigurationNavigationApiContext);
}

interface SubconfigurationNavigationApiProviderProps extends SubconfigurationNavigationApi {
  children: ReactNode;
}

/**
 * Propagates the {@link SubconfigurationNavigationApi} via context
 * @param props
 * @returns
 */
export function SubconfigurationNavigationApiProvider(
  props: SubconfigurationNavigationApiProviderProps,
): JSX.Element {
  const { children, previous, next, previousDisabled, nextDisabled } = props;

  // memoized the individual functions and properties to avoid unnecessary re-renders
  const api = useMemo(
    () => ({
      previous,
      next,
      previousDisabled,
      nextDisabled,
    }),
    [previous, next, previousDisabled, nextDisabled],
  );

  return (
    <SubconfigurationNavigationApiContext.Provider value={api}>
      {children}
    </SubconfigurationNavigationApiContext.Provider>
  );
}
